import { GET, POST, DELETE } from '@/shared/api/requests';
import {
  DroneVideo,
  DroneVideoFormData,
  DroneVideoCreateResponse,
  SignedUrlResponse,
  DroneTypeChoice,
  DroneSkyMapUploadFormData,
  DroneSkyMapUploadResponse,
  Drone360VideoUploadFormData,
  Drone360VideoUploadResponse,
} from '../types';

/**
 * Get available drone type choices
 */
export const getDroneTypes = async (): Promise<DroneTypeChoice[]> => {
  console.log('🔍 getDroneTypes called');
  try {
    const response = await GET('/drones/drone-types/');
    console.log('✅ getDroneTypes raw response:', response);
    console.log('✅ getDroneTypes response.data:', response.data);
    console.log('✅ getDroneTypes response.data.choices:', response.data.choices);
    return response.data.choices; // Backend returns {choices: [...]}
  } catch (error: any) {
    console.error('❌ getDroneTypes error:', error);
    throw error;
  }
};

/**
 * Get a signed URL for a supported drone media upload.
 */
export const getSignedUrlForDroneUpload = async (
  projectId: number,
  droneType: string,
  date: string,
  fileName: string = 'upload.zip',
  uploadKind: 'project_zip' | 'gps' | 'sky_map_source' | '360_video_source' = 'project_zip'
): Promise<SignedUrlResponse> => {
  const response = await POST('/drones/get-signed-url/', {
    project_id: projectId,
    drone_type: droneType,
    date: date,
    file_name: fileName,
    upload_kind: uploadKind,
  });
  return response.data;
};

export const getSignedUrlForDroneZip = getSignedUrlForDroneUpload;

/**
 * Upload a drone media file to GCS using a signed URL.
 */
export const uploadFileToGCS = async (
  file: File,
  signedUrl: string,
  onProgress?: (progress: number) => void,
  contentType: string = 'application/zip'
): Promise<void> => {
  console.log('📤 uploadZipToGCS called with:', { fileName: file.name, fileSize: file.size, signedUrl });
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          console.log(`📊 Upload progress: ${progress.toFixed(2)}%`);
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      console.log(`✅ XHR load event - status: ${xhr.status}, statusText: ${xhr.statusText}`);
      if (xhr.status === 200) {
        console.log('✅ Upload successful');
        resolve();
      } else {
        console.error(`❌ Upload failed with status ${xhr.status}:`, xhr.responseText);
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', (e) => {
      console.error('❌ XHR error event:', e);
      reject(new Error('Upload failed: Network error'));
    });

    xhr.addEventListener('abort', () => {
      console.error('❌ XHR abort event');
      reject(new Error('Upload aborted'));
    });

    console.log('🚀 Starting XHR upload...');
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
};

export const uploadZipToGCS = uploadFileToGCS;

/**
 * Create drone video record after zip upload
 */
export const createDroneVideo = async (
  droneData: DroneVideoFormData
): Promise<DroneVideoCreateResponse> => {
  console.log('💾 createDroneVideo called with:', droneData);
  try {
    const response = await POST('/drones/create/', droneData);
    console.log('✅ createDroneVideo response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ createDroneVideo error:', {
      message: error.message,
      response: error.response?.data,
      responseData: error.response,
      status: error.response?.status,
      fullError: error
    });
    throw error;
  }
};

export const createDroneSkyMapUpload = async (
  uploadData: DroneSkyMapUploadFormData
): Promise<DroneSkyMapUploadResponse> => {
  const response = await POST('/drones/sky-map-uploads/', uploadData);
  return response.data;
};

export const createDrone360VideoUpload = async (
  uploadData: Drone360VideoUploadFormData
): Promise<Drone360VideoUploadResponse> => {
  const response = await POST('/drones/360-video-uploads/', uploadData);
  return response.data;
};

/**
 * Get list of drone videos (optionally filtered)
 */
export const getDroneVideos = async (
  projectId?: number,
  droneType?: string,
  date?: string
): Promise<DroneVideo[]> => {
  let url = '/drones/list/?';
  const params = new URLSearchParams();
  
  if (projectId) params.append('project_id', projectId.toString());
  if (droneType) params.append('drone_type', droneType);
  if (date) params.append('date', date);
  
  const response = await GET(`${url}${params.toString()}`);
  return response.data;
};

/**
 * Get a specific drone video by ID
 */
export const getDroneVideo = async (id: number): Promise<DroneVideo> => {
  const response = await GET(`/drones/${id}/`);
  return response.data;
};

/**
 * Delete a drone video
 */
export const deleteDroneVideo = async (
  id: number,
  deleteFiles: boolean = false
): Promise<void> => {
  const url = deleteFiles ? `/drones/${id}/?delete_files=true` : `/drones/${id}/`;
  await DELETE(url);
};
